import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Plus } from "@phosphor-icons/react";

export default function ClientProgress() {
  const [entries, setEntries] = useState([]);
  const [weight, setWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/client/progress");
      setEntries(data);
    } catch (e) { setErr(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!weight) return;
    setSaving(true); setErr("");
    try {
      await http.post("/client/progress", { weight_kg: Number(weight), notes });
      setWeight(""); setNotes("");
      await load();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const chartData = entries.map((p) => ({ date: (p.date || "").slice(5, 10), weight: p.weight_kg }));

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <div className="id-label mb-3">— Tracking —</div>
      <h1 className="id-h1 mb-10">Progress</h1>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}

      <div className="id-card mb-6">
        <div className="id-label mb-3">Body Weight Trend</div>
        {chartData.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-zinc-500 font-manrope text-sm">
            Log your first weigh-in to see the chart.
          </div>
        ) : (
          <div className="h-56" data-testid="progress-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#262626" vertical={false} />
                <XAxis dataKey="date" stroke="#525252" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                <YAxis stroke="#525252" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 0, fontFamily: "Manrope" }} />
                <Line type="monotone" dataKey="weight" stroke="#FF3333" strokeWidth={2} dot={{ fill: "#fff", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="id-card mb-6">
        <div className="id-label mb-3">Log Today's Weight</div>
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-12 gap-3" data-testid="progress-form">
          <input
            type="number" step={0.1} min={0} required
            value={weight} onChange={(e) => setWeight(e.target.value)}
            placeholder="Weight (kg)" className="id-input md:col-span-3"
            data-testid="progress-weight-input"
          />
          <input
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)" className="id-input md:col-span-7"
            data-testid="progress-notes-input"
          />
          <button type="submit" disabled={saving} className="id-btn-primary md:col-span-2" data-testid="progress-save-button">
            <Plus size={16} weight="bold" /> {saving ? "..." : "Add"}
          </button>
        </form>
      </div>

      <div className="id-card">
        <div className="id-label mb-3">History</div>
        {entries.length === 0 ? (
          <div className="text-zinc-500 font-manrope text-sm">Nothing logged yet.</div>
        ) : (
          <ul className="divide-y divide-[#262626]" data-testid="progress-history">
            {[...entries].reverse().map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between text-sm font-manrope">
                <div>
                  <div className="font-semibold">{p.weight_kg} kg</div>
                  {p.notes && <div className="text-zinc-500 text-xs">{p.notes}</div>}
                </div>
                <div className="id-label">{new Date(p.date).toLocaleDateString()}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
